/** Course switcher. Languages without a published course are shown as «скоро». */
import { Sheet } from '../ui/Sheet.tsx';
import { RoboCat } from '../ui/RoboCat.tsx';
import { Icon } from '../ui/Icon.tsx';
import { LANGUAGES } from '../ui/course.ts';
import { useAppData, useLoadedData } from '../state/app-data.tsx';
import { useCoursePath } from '../state/selectors.ts';
import { useSession } from '../state/session.tsx';
import { routes, useRouter } from '../state/router.tsx';
import { PLACEMENT_LANGS, cachedPlacements, placementDismissed } from '../data/placement.ts';
import './course-sheet.css';

export function CourseSheet({ onClose }: { onClose: () => void }) {
  const { courses, activeCourseId, allBooks, allLessons, progress } = useLoadedData();
  const { setActiveCourse } = useAppData();
  const { navigate } = useRouter();
  const userId = useSession().session?.user.id ?? '';
  const path = useCoursePath();
  // Every course's lessons are already loaded, so switching is instant (saved in the background).
  const choose = (courseId: string) => {
    if (courseId !== activeCourseId) setActiveCourse(courseId);
    onClose();
    // First time in a language (no test, no lesson started): offer the placement test.
    const lang = courses.find((c) => c.id === courseId)?.lang ?? '';
    const bookIds = new Set(allBooks.filter((b) => b.course_id === courseId).map((b) => b.id));
    const started = allLessons.some((l) => bookIds.has(l.book_id) && progress[l.id]);
    if (
      courseId !== activeCourseId &&
      PLACEMENT_LANGS.includes(lang) &&
      !started &&
      !cachedPlacements(userId)[lang] &&
      !placementDismissed(userId, lang)
    ) {
      navigate(routes.placement);
    }
  };

  return (
    <Sheet title="Курс" onClose={onClose}>
      <h2 className="t-card">Курс</h2>
      <p className="t-meta course-sheet__lead">Учитель подстроится под язык курса.</p>
      <ul className="course-sheet__list">
        {LANGUAGES.map((language) => {
          const course = courses.find((c) => c.lang === language.lang);
          const active = course !== undefined && course.id === activeCourseId;
          const meta = !course
            ? 'Появится позже'
            : active && path.items.length > 0
              ? `${course.title} · ${path.bookTitle ?? ''} · ${path.doneCount} из ${path.items.length}`
              : course.title;
          return (
            <li key={language.lang} className={language.theme}>
              <button
                type="button"
                className={`course-card${active ? ' course-card--active' : ''}`}
                disabled={!course}
                aria-pressed={active}
                onClick={() => course && choose(course.id)}
              >
                <RoboCat size={62} />
                <span className="course-card__text">
                  <span className="course-card__title">
                    {language.titleRu}
                    {!course && <span className="chip course-card__soon">скоро</span>}
                  </span>
                  <span className="t-meta">{meta}</span>
                  <span className="course-card__dots" aria-hidden="true">
                    <i style={{ background: 'var(--c-a1)' }} />
                    <i style={{ background: 'var(--c-a2)' }} />
                    <i style={{ background: 'var(--c-a3)' }} />
                  </span>
                </span>
                <span className="course-card__radio" aria-hidden="true">
                  {active && <Icon name="check" size={16} />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
}
